// Carry Roll Optimization engine — inverse solver + optimizers (4.2.8, 10.7–10.9). Pure functions.
import { simulate } from './simulate';
import { brent } from '../lib/brent';
import { degToRad, radToDeg } from './units';
/**
 * Back-solve the launch angle that produces a target carry.
 * Uses Brent's method on the forward simulate. Round-trip verifies.
 * If unreachable → returns nearest + verified:false.
 * If multi-solution → returns lower-launch branch.
 */
export function solveInverse(targetCarryM, fixed, _freeVars) {
    const carryAt = (launchDeg) => {
        const req = {
            ...fixed,
            launch: { ...fixed.launch, launchAngle: degToRad(launchDeg) },
        };
        return simulate(req).carryM;
    };
    // Scan the feasible range to find the carry envelope.
    const lo = 1;
    const hi = 55;
    const steps = 28;
    let maxCarry = 0;
    let maxDeg = 12;
    for (let i = 0; i <= steps; i++) {
        const deg = lo + ((hi - lo) * i) / steps;
        const c = carryAt(deg);
        if (c > maxCarry) {
            maxCarry = c;
            maxDeg = deg;
        }
    }
    // If target exceeds the maximum achievable carry, return nearest + unverified.
    if (targetCarryM > maxCarry) {
        return {
            launchAngleDeg: Math.round(maxDeg * 10) / 10,
            verified: false,
            carryM: Math.round(maxCarry),
            explanation: `Target ${Math.round(targetCarryM)} m exceeds maximum achievable carry ${Math.round(maxCarry)} m at ${maxDeg.toFixed(1)}° launch for this ball speed.`,
        };
    }
    // There are typically two launch angles producing the same sub-max carry
    // (one on each side of the optimum). We want the lower-launch branch.
    // Search in [lo, maxDeg] for the sign change.
    const findRoot = (a, b) => {
        return brent((deg) => carryAt(deg) - targetCarryM, a, b, 1e-3, 80);
    };
    let root = findRoot(lo, maxDeg);
    if (root == null) {
        root = findRoot(maxDeg, hi);
    }
    if (root == null) {
        return {
            launchAngleDeg: maxDeg,
            verified: false,
            carryM: maxCarry,
            explanation: 'No launch angle reproduces the target within the feasible range.',
        };
    }
    // Round-trip verify
    const verifiedCarry = carryAt(root);
    const verified = Math.abs(verifiedCarry - targetCarryM) < 0.5;
    return {
        launchAngleDeg: Math.round(root * 10) / 10,
        verified,
        carryM: Math.round(verifiedCarry),
        explanation: verified
            ? `Solved launch ${root.toFixed(1)}° reproduces ${Math.round(verifiedCarry)} m (target ${Math.round(targetCarryM)} m).`
            : `Nearest launch ${root.toFixed(1)}° produces ${Math.round(verifiedCarry)} m; could not match ${Math.round(targetCarryM)} m within 0.5 m.`,
    };
}
/**
 * Back-solve the ball speed that produces a target carry at a fixed launch angle.
 * Varies ball speed over [10, 100] m/s via Brent's method. Round-trip verifies
 * within 0.5 m. Returns verified:false when the target exceeds the carry at
 * 100 m/s ball speed. `launchAngleDeg` is populated with the fixed launch for
 * display continuity; `ballSpeedMs` carries the solved ball speed.
 */
export function solveInverseBallSpeed(targetCarryM, fixed) {
    const fixedLaunchDeg = Math.round(radToDeg(fixed.launch.launchAngle) * 10) / 10;
    const carryAt = (ballSpeed) => {
        const req = {
            ...fixed,
            launch: { ...fixed.launch, ballSpeed },
        };
        return simulate(req).carryM;
    };
    const lo = 10;
    const hi = 100;
    const maxCarry = carryAt(hi);
    if (targetCarryM > maxCarry) {
        return {
            launchAngleDeg: fixedLaunchDeg,
            verified: false,
            carryM: Math.round(maxCarry),
            ballSpeedMs: hi,
            explanation: `Target ${Math.round(targetCarryM)} m exceeds maximum achievable carry ${Math.round(maxCarry)} m at ${hi} m/s ball speed.`,
        };
    }
    const root = brent((bs) => carryAt(bs) - targetCarryM, lo, hi, 1e-3, 80);
    if (root == null) {
        return {
            launchAngleDeg: fixedLaunchDeg,
            verified: false,
            carryM: Math.round(maxCarry),
            ballSpeedMs: hi,
            explanation: 'No ball speed reproduces the target within [10, 100] m/s.',
        };
    }
    const verifiedCarry = carryAt(root);
    const verified = Math.abs(verifiedCarry - targetCarryM) < 0.5;
    return {
        launchAngleDeg: fixedLaunchDeg,
        verified,
        carryM: Math.round(verifiedCarry),
        ballSpeedMs: Math.round(root * 10) / 10,
        explanation: verified
            ? `Solved ball speed ${root.toFixed(1)} m/s reproduces ${Math.round(verifiedCarry)} m (target ${Math.round(targetCarryM)} m).`
            : `Nearest ball speed ${root.toFixed(1)} m/s produces ${Math.round(verifiedCarry)} m; could not match ${Math.round(targetCarryM)} m within 0.5 m.`,
    };
}
export function teeOptimizer(base, constraints) {
    const setups = [];
    // Sweep launch angle and spin around the base request.
    const launchDegs = [8, 10, 12, 14, 16, 18];
    const spinMults = [0.8, 1.0, 1.2];
    for (const ld of launchDegs) {
        for (const sm of spinMults) {
            const req = {
                ...base,
                launch: {
                    ...base.launch,
                    launchAngle: degToRad(ld),
                    spinRpm: Math.round(base.launch.spinRpm * sm),
                },
            };
            const result = simulate(req);
            const playable = Math.abs(result.offlineM) <= constraints.offlineToleranceM;
            setups.push({
                ballSpeed: base.launch.ballSpeed,
                launchAngleDeg: ld,
                spinRpm: req.launch.spinRpm,
                totalM: Math.round(result.totalM),
                carryM: Math.round(result.carryM),
                offlineM: Math.round(result.offlineM),
                playable,
                score: playable ? result.totalM : -1,
            });
        }
    }
    // Rank by total distance among playable, then by closeness to center.
    setups.sort((a, b) => {
        if (a.playable !== b.playable)
            return a.playable ? -1 : 1;
        if (a.playable)
            return b.totalM - a.totalM;
        return Math.abs(a.offlineM) - Math.abs(b.offlineM);
    });
    return setups;
}
export function approachOptimizer(base, constraints) {
    const setups = [];
    const ballSpeeds = [base.launch.ballSpeed * 0.9, base.launch.ballSpeed, base.launch.ballSpeed * 1.1];
    const launchDegs = [20, 28, 36, 44];
    const spinMults = [1.0, 1.3, 1.6];
    for (const bs of ballSpeeds) {
        for (const ld of launchDegs) {
            for (const sm of spinMults) {
                const req = {
                    ...base,
                    launch: {
                        ...base.launch,
                        ballSpeed: bs,
                        launchAngle: degToRad(ld),
                        spinRpm: Math.round(base.launch.spinRpm * sm),
                    },
                };
                const result = simulate(req);
                // Stopping power: higher landing angle + more spin = more stopping
                const stoppingPower = result.landingAngleDeg * 0.4 + (req.launch.spinRpm / 1000) * 0.6;
                const carryError = Math.abs(result.carryM - constraints.targetCarryM);
                // Score: closer to target carry + higher stopping power
                const score = -carryError + stoppingPower;
                setups.push({
                    ballSpeed: Math.round(bs * 10) / 10,
                    launchAngleDeg: ld,
                    spinRpm: req.launch.spinRpm,
                    carryM: Math.round(result.carryM),
                    landingAngleDeg: Math.round(result.landingAngleDeg),
                    stoppingPower: Math.round(stoppingPower * 10) / 10,
                    score: Math.round(score * 10) / 10,
                });
            }
        }
    }
    setups.sort((a, b) => b.score - a.score);
    return setups.slice(0, 10);
}
export function fittingMode(request) {
    const baseline = simulate(request).carryM;
    const gains = [];
    // Test ball speed +2 m/s
    const bsResult = simulate({
        ...request,
        launch: { ...request.launch, ballSpeed: request.launch.ballSpeed + 2 },
    });
    gains.push({
        parameter: 'Ball speed',
        delta: '+2 m/s',
        current: request.launch.ballSpeed,
        optimized: request.launch.ballSpeed + 2,
        expectedGainM: Math.round((bsResult.carryM - baseline) * 10) / 10,
    });
    // Test launch angle optimized
    const inverse = solveInverse(baseline * 1.02, request);
    if (inverse.verified) {
        gains.push({
            parameter: 'Launch angle',
            delta: `→ ${inverse.launchAngleDeg}°`,
            current: Math.round(radToDeg(request.launch.launchAngle) * 10) / 10,
            optimized: inverse.launchAngleDeg,
            expectedGainM: Math.round(baseline * 0.02 * 10) / 10,
        });
    }
    // Test spin reduction 500 rpm
    const spinResult = simulate({
        ...request,
        launch: { ...request.launch, spinRpm: Math.max(0, request.launch.spinRpm - 500) },
    });
    gains.push({
        parameter: 'Spin rate',
        delta: '-500 rpm',
        current: request.launch.spinRpm,
        optimized: Math.max(0, request.launch.spinRpm - 500),
        expectedGainM: Math.round((spinResult.carryM - baseline) * 10) / 10,
    });
    // Sort by expected gain descending
    gains.sort((a, b) => b.expectedGainM - a.expectedGainM);
    return gains;
}
/**
 * Find the launch angle (4°..45°) that maximises carry while keeping the apex
 * under `ceilingM`. If no launch clears the ceiling, returns feasible:false
 * with the lowest-apex launch found.
 */
export function apexConstrainedCarry(request, ceilingM) {
    const lo = 4;
    const hi = 45;
    const steps = 41; // ~1° increments
    let best = null; // max carry among feasible
    let lowest = null; // lowest apex overall
    for (let i = 0; i <= steps; i++) {
        const deg = lo + ((hi - lo) * i) / steps;
        const result = simulate({
            ...request,
            launch: { ...request.launch, launchAngle: degToRad(deg) },
        });
        const entry = {
            launchAngleDeg: Math.round(deg * 10) / 10,
            carryM: Math.round(result.carryM),
            apexM: result.apexM,
            feasible: result.apexM <= ceilingM,
        };
        if (lowest == null || entry.apexM < lowest.apexM) {
            lowest = entry;
        }
        if (entry.feasible && (best == null || entry.carryM > best.carryM)) {
            best = entry;
        }
    }
    if (best != null)
        return best;
    return {
        launchAngleDeg: lowest.launchAngleDeg,
        carryM: lowest.carryM,
        apexM: lowest.apexM,
        feasible: false,
    };
}
//# sourceMappingURL=optimize.js.map